import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface TranslationState {
  translations: Record<string, string>;
  loading: boolean;
  error: string | null;
  currentLang: string;
  _hasHydrated: boolean;
  fetchTranslations: (lang: string, prefix?: string) => Promise<boolean>;
  refreshTranslations: () => Promise<void>;
  setLang: (lang: string) => void;
  setHasHydrated: (state: boolean) => void;
}

const STORE_VERSION = 1;

const sameTranslations = (
  current: Record<string, string>,
  incoming: Record<string, string>
): boolean => {
  const incomingKeys = Object.keys(incoming);
  return (
    incomingKeys.length === Object.keys(current).length &&
    incomingKeys.every(key => current[key] === incoming[key])
  );
};

const getInitialTranslationState = () => ({
  translations: {},
  loading: false,
  error: null,
  currentLang: process.env.NEXT_PUBLIC_DEFAULT_LANG || 'ca',
  _hasHydrated: false,
});

export const useTranslationStore = create<TranslationState>()(
  persist(
    (set, get) => ({
      ...getInitialTranslationState(),
      fetchTranslations: async (lang, prefix = '') => {
        set({ loading: true, error: null });
        try {
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_API_BASE_URL}Config/Translations?lang=${lang}&key=${prefix}`
          );
          const data = await response.json();
          set({ translations: data.translations, loading: false });
          return Object.keys(data.translations).length > 0;
        } catch (err) {
          set({ error: 'Failed to load translations', loading: false });
          return false;
        }
      },
      refreshTranslations: async () => {
        try {
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_API_BASE_URL}Config/Translations?lang=${get().currentLang}&key=`
          );
          if (!response.ok) return;
          const data: { translations?: Record<string, string> } =
            await response.json();
          const incoming = data.translations;
          if (!incoming || Object.keys(incoming).length === 0) return;
          if (sameTranslations(get().translations, incoming)) return;
          set({ translations: incoming });
        } catch (error) {
          console.error('Error refreshing translations:', error);
        }
      },
      setLang: lang => set({ currentLang: lang }),
      setHasHydrated: state => set({ _hasHydrated: state }),
    }),
    {
      name: 'translation-store',
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        translations: state.translations,
        currentLang: state.currentLang,
      }),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true);
      },
      migrate: persistedState => {
        // Conservar la caché de traducciones entre builds; solo descartar
        // datos corruptos. El refresco de traducciones es un acto deliberado.
        const state = persistedState as Partial<TranslationState>;
        if (!state || typeof state.translations !== 'object') {
          return getInitialTranslationState() as TranslationState;
        }
        return {
          ...getInitialTranslationState(),
          translations: state.translations || {},
          currentLang:
            state.currentLang || process.env.NEXT_PUBLIC_DEFAULT_LANG || 'ca',
        } as TranslationState;
      },
    }
  )
);
